import React, {useEffect, useRef} from 'react';
import {StyleSheet} from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import {observer} from 'mobx-react-lite';

import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import Text from '../ui-controls/Text';

/** Animation config for smooth sliding transition */
const SLIDE_CONFIG = {
  duration: 200,
  easing: Easing.out(Easing.cubic),
};

/** Distance to slide in pixels */
const SLIDE_DISTANCE = 12;

const CartCountBadge = observer(function CartCountBadge() {
  const {cartStore} = useStores();
  const {isWebDesktopScreen} = useWebScreenDimensions();

  const translateX = useSharedValue(0);
  const opacity = useSharedValue(1);
  const prevCountRef = useRef<number | null>(null);

  const styles = isWebDesktopScreen ? webDesktopStyles : nativeStyles;

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{translateX: translateX.value}],
    opacity: opacity.value,
  }));

  const isLoading = cartStore.updatingOrder || cartStore.isLoadingOrder;
  const currentCount = isLoading ? null : cartStore.getTotalOrderCount();

  // Trigger slide animation when count changes
  useEffect(() => {
    if (currentCount !== null && prevCountRef.current !== null) {
      const isIncreasing = currentCount > prevCountRef.current;
      const isDecreasing = currentCount < prevCountRef.current;

      if (isIncreasing || isDecreasing) {
        // Start position: slide in from opposite direction
        // Increase: slide from right (positive) to center
        // Decrease: slide from left (negative) to center
        const startOffset = isIncreasing ? SLIDE_DISTANCE : -SLIDE_DISTANCE;

        // Quick fade out and position reset
        opacity.value = 0;
        translateX.value = startOffset;

        // Animate in
        opacity.value = withTiming(1, SLIDE_CONFIG);
        translateX.value = withTiming(0, SLIDE_CONFIG);
      }
    }
    if (currentCount !== null) {
      prevCountRef.current = currentCount;
    }
  }, [currentCount, translateX, opacity]);

  return (
    <Animated.View
      testID="cart-count-badge"
      style={[!cartStore.dirtyOrder ? styles.badge : [styles.badge, styles.dirty]]}>
      <Animated.View style={animatedStyle}>
        <Text
          variant="caption"
          style={!cartStore.dirtyOrder ? styles.badgeText : [styles.badgeText, styles.dirtyText]}>
          {isLoading ? '..' : currentCount}
        </Text>
      </Animated.View>
    </Animated.View>
  );
});

export default CartCountBadge;

const webDesktopStyles = StyleSheet.create({
  badge: {
    position: 'absolute',
    backgroundColor: theme.colour.general.secondary,
    paddingHorizontal: 6,
    borderRadius: 8,
    right: 16,
    top: 10,
    overflow: 'hidden',
  },
  dirty: {
    backgroundColor: theme.colour.general.secondary,
  },
  badgeText: {
    color: theme.colour.general.primary,
  },
  dirtyText: {
    color: theme.colour.general.highlight,
  },
});

const nativeStyles = StyleSheet.create({
  badge: {
    position: 'absolute',
    backgroundColor: theme.colour.general.primary,
    paddingHorizontal: 6,
    borderRadius: 8,
    right: 8,
    top: 4,
    overflow: 'hidden',
  },
  dirty: {
    backgroundColor: theme.colour.general.secondary,
  },
  badgeText: {
    color: theme.colour.text.tabIcons.cart.priceBadge,
  },
  dirtyText: {
    color: theme.colour.general.primary,
  },
});
